import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { Player } from 'src/app/features/handball/player/model/player';
import { Card } from 'src/app/features/handball/cards/model/card';
import { SmallCardComponent } from 'src/app/features/handball/cards/view/small-card/small-card.component';
import { SpiderChartComponent } from 'src/app/features/graphs/view/spider-chart/spider-chart.component';
import { SpiderChartModel } from 'src/app/features/graphs/model/spider-chart/spider-chart-model';
import { DonutChartComponent } from '../../../../graphs/view/donut-chart/donut-chart.component';
import { DonutChartModel } from 'src/app/features/graphs/model/donut-chart/donut-chart-model';
import { LineChartComponent } from '../../../../graphs/view/line-chart/line-chart.component';
import { LineChartModel } from 'src/app/features/graphs/model/line-chart/line-chart-model';
import { PlayerBasicInfoComponent } from '../player-basic-info/player-basic-info.component';
import { TableModule } from 'primeng/table';
import { PlayerMatchData } from '../../model/player-match-data';
import { CardGridComponent } from '../../../card-grid/card-grid.component';

@Component({
  selector: 'app-player-stats',
  templateUrl: 'player-stats.component.html',
  styleUrls: ['player-stats.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    PlayerBasicInfoComponent,
    SmallCardComponent,
    SpiderChartComponent,
    DonutChartComponent,
    LineChartComponent,
    TableModule,
    CardGridComponent
  ],
})
export class PlayerStatsComponent implements OnInit, OnChanges {
  @Input() player: Player;

  cards: Card[] = [];

  // Chart models
  strengthAndWeaknessSpiderChartData: SpiderChartModel;
  passDistributionData: DonutChartModel;
  playerPerformanceLast5MatchesLinePlotData: LineChartModel;
  playerPerformanceLast5MatchesTableData: PlayerMatchData[] = [];

  constructor() {}

  async ngOnChanges() {
    if (this.player) {
      this.getCards();
    }
    this.updateGraphs(this.player);
  }

  async ngOnInit() {
    if (this.player) {
      this.getCards();
    }
    this.updateGraphs(this.player);
  }

  private async updateGraphs(p: Player) {
    this.updateStrengthAndWeaknessSpiderChartData();
    this.updatePassDistributionData(p);
    this.updatePlayerPerformanceLast5MatchesTableData();
  }

  private async getCards() {
    this.cards = [
      {
        id: '1',
        label: 'Partidos Jugados',
        value: this.player.stats.totalMatchesPlayed,
      },
      {
        id: '2',
        label: 'Minutos Jugados',
        value: this.player.stats.totalMinutesPlayed,
      },
      // {
      //   id: "3",
      //   label: 'Metros recorridos',
      //   value: this.player.stats.totalMetersRun,
      // },
      // {
      //   id: "4",
      //   label: 'Máx. Velocidad',
      //   value: this.player.stats.maxSpeedInKmPerHour + " km/h",
      // },
    ];

    if (this.player.position === 'Portero') {
      this.cards.push(
        ...[
          {
            id: '10',
            label: 'Goles Recibidos',
            value: this.player.stats.totalGoalsReceived,
          },
          {
            id: '9',
            label: 'Paradas',
            value: this.player.stats.totalSaves,
          },
        ]
      );
    } else {
      this.cards.push(
        ...[
          {
            id: '5',
            label: 'Lanzamientos',
            value: this.player.stats.totalShots,
          },
          {
            id: '6',
            label: 'Pases',
            value: this.player.stats.totalPasses,
          },
          {
            id: '7',
            label: 'Asistencias',
            value: this.player.stats.totalAssists,
          },
          {
            id: '8',
            label: 'Goles',
            value: this.player.stats.totalGoals,
          },
        ]
      );
    }
  }

  private async updatePlayerPerformanceLast5MatchesTableData() {
    this.playerPerformanceLast5MatchesTableData = [
      {
        homeTeam: 'España',
        awayTeam: 'Suecia',
        goalScored: 2,
        assists: 1,
        turnovers: 0,
        sanctions: 1,
        minutesPlayed: 60,
      },
      {
        homeTeam: 'Suecia',
        awayTeam: 'España',
        goalScored: 3,
        assists: 2,
        turnovers: 1,
        sanctions: 0,
        minutesPlayed: 60,
      },
      {
        homeTeam: 'Francia',
        awayTeam: 'España',
        goalScored: 1,
        assists: 0,
        turnovers: 2,
        sanctions: 1,
        minutesPlayed: 60,
      },
      {
        homeTeam: 'España',
        awayTeam: 'Dinamarca',
        goalScored: 4,
        assists: 1,
        turnovers: 1,
        sanctions: 0,
        minutesPlayed: 60,
      },
      {
        homeTeam: 'España',
        awayTeam: 'Noruega',
        goalScored: 2,
        assists: 3,
        turnovers: 0,
        sanctions: 1,
        minutesPlayed: 60,
      },
    ];
  }

  private async updatePassDistributionData(p: Player) {
    const a = p.stats.shotAccuracyDistribution.accurateShotsPercent;
    const m = p.stats.shotAccuracyDistribution.missedShotsPercent;
    const i = p.stats.shotAccuracyDistribution.interceptedShotsPercent;
    const av = Math.round(a * p.stats.totalPasses /100)
    const mv = Math.round(m * p.stats.totalPasses /100)
    const iv = Math.round(i * p.stats.totalPasses /100)
    const total = av + mv + iv

    this.passDistributionData = {
      title: 'Estadísticas de Pases',
      items: [
        {
          label: 'Pases totales',
          value: total,
          color: '#000000',
        },
        {
          label: 'Completados',
          value: av,
          amount: a,
          color: '#19284c',
        },
        {
          label: 'Interceptados',
          value: mv,
          amount: m,
          color: '#47050b',
        },
        {
          label: 'Fallidos',
          value: iv,
          amount: i,
          color: '#fe192f',
        },
      ],
    };
  }

  private async updateStrengthAndWeaknessSpiderChartData() {
    const playerName = this.player.fullName;

    this.strengthAndWeaknessSpiderChartData = {
      title: 'Estadísticas generales',
      series: [
        {
          name: playerName,
          data: [
            { label: 'Goles', value: 4.8 },
            { label: 'Eficacia', value: 2.9 },
            { label: 'Sanciones', value: 2.5 },
            { label: 'Faltas', value: 4 },
            { label: 'Asistencias', value: 3 },
            { label: 'Pérdidas', value: 4.5 },
          ],
        },
        {
          name: 'Media del Equipo',
          data: [
            { label: 'Goles', value: 3.6 },
            { label: 'Eficacia', value: 3.8 },
            { label: 'Sanciones', value: 4.5 },
            { label: 'Faltas', value: 2.5 },
            { label: 'Asistencias', value: 3.8 },
            { label: 'Pérdidas', value: 3 },
          ],
        },
      ],
    };
  }
}

